import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CalendarPicker } from '@mui/lab';
import { Box } from '@mui/material';
import { sub } from 'date-fns';
import add from 'date-fns/add';
import { memo, useCallback, useMemo } from 'react';

import { useCurrentUserClient } from '../../../contexts/OidcContext/OidcContext.selectors';
import { useDateFnsFormat } from '../../../hooks/useDateFnsFormat';
import { useRmsGanttState } from '../../Rms/RmsContent/RmsContext';
import { PopoverMenu } from '../PopoverMenu/PopoverMenu';
import { DateBox, DayBox, HandleIconButton, iconStyle, NavBox } from './SelectedDate.style';
import {
    getAuthorizedPeriodForClient,
    getMaxAccessibleDateForClient,
    getMinAccessibleDateForClient,
} from './SelectedDate.utils';

export const DATE_FORMAT = 'dd LLL y';

export const SelectedDate = memo(function SelectedDate({ disabled }: { disabled?: boolean }) {
    const [day, setDay] = useRmsGanttState('day');
    const [rmsFlights, setRmsFilters] = useRmsGanttState('rmsFilters');
    const format = useDateFnsFormat();
    const client = useCurrentUserClient();

    const minDate = useMemo(() => {
        return getMinAccessibleDateForClient(client.permissions, client.nbDaysToRetrieveFlights);
    }, [client.nbDaysToRetrieveFlights, client.permissions]);
    const maxDate = useMemo(() => {
        return getMaxAccessibleDateForClient(client.permissions, client.nbDaysToRetrieveFlights);
    }, [client.nbDaysToRetrieveFlights, client.permissions]);
    const authorizedPeriod = getAuthorizedPeriodForClient(client.permissions, client.nbDaysToRetrieveFlights);

    const handleNext = useCallback(() => {
        setDay(add(day, { days: 1 }));
        setRmsFilters({
            ...rmsFlights,
            viewModeScheduled: 'deviations',
        });
    }, [day, rmsFlights, setDay, setRmsFilters]);

    const handlePrevious = useCallback(() => {
        setDay(add(day, { days: -1 }));
        setRmsFilters({
            ...rmsFlights,
            viewModeScheduled: 'deviations',
        });
    }, [day, rmsFlights, setDay, setRmsFilters]);

    const handleNextWeek = useCallback(() => {
        setDay(add(day, { days: 7 }));
        setRmsFilters({
            ...rmsFlights,
            viewModeScheduled: 'deviations',
        });
    }, [day, rmsFlights, setDay, setRmsFilters]);

    const handlePreviousWeek = useCallback(() => {
        setDay(add(day, { days: -7 }));
        setRmsFilters({
            ...rmsFlights,
            viewModeScheduled: 'deviations',
        });
    }, [day, rmsFlights, setDay, setRmsFilters]);

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} mt={2}>
            <DayBox>{format(day, 'EEEE')}</DayBox>
            <NavBox>
                <HandleIconButton
                    onClick={handlePreviousWeek}
                    disabled={disabled || authorizedPeriod <= 7 || day < add(minDate, { days: 7 })}
                >
                    <ChevronLeftIcon
                        sx={{
                            ...iconStyle,
                            marginRight: '-13px',
                        }}
                    />
                    <ChevronLeftIcon
                        sx={{
                            ...iconStyle,
                            marginLeft: '-13px',
                        }}
                    />
                </HandleIconButton>
                <HandleIconButton onClick={handlePrevious} disabled={disabled || day <= minDate}>
                    <ChevronLeftIcon sx={iconStyle} />
                </HandleIconButton>
                &emsp;
                <PopoverMenu
                    renderButton={({ onClick }) => (
                        <DateBox
                            onClick={disabled ? undefined : onClick}
                            sx={{ cursor: disabled ? 'default' : 'pointer' }}
                        >
                            {format(day, DATE_FORMAT).replace('.', '')}
                        </DateBox>
                    )}
                >
                    {({ close }) => (
                        <CalendarPicker
                            minDate={minDate}
                            maxDate={maxDate}
                            date={day}
                            onChange={date => {
                                close();
                                if (date) {
                                    setDay(date);
                                    setRmsFilters({
                                        ...rmsFlights,
                                        viewModeScheduled: 'deviations',
                                    });
                                }
                            }}
                        />
                    )}
                </PopoverMenu>
                &emsp;
                <HandleIconButton
                    sx={{ padding: 0 }}
                    onClick={handleNext}
                    disabled={disabled || day > sub(maxDate, { days: 1 })}
                >
                    <ChevronRightIcon sx={iconStyle} />
                </HandleIconButton>
                <HandleIconButton
                    onClick={handleNextWeek}
                    disabled={disabled || authorizedPeriod <= 7 || day > sub(maxDate, { days: 7 })}
                >
                    <ChevronRightIcon
                        sx={{
                            ...iconStyle,
                            marginRight: '-13px',
                        }}
                    />
                    <ChevronRightIcon
                        sx={{
                            ...iconStyle,
                            marginLeft: '-13px',
                        }}
                    />
                </HandleIconButton>
            </NavBox>
        </Box>
    );
});
